import React, { Fragment, useEffect, useState } from 'react'
import { UPDATE_QUESTION_ACTIONS } from '../../model/editableQuestion/actions'
import { TeacherQuestionProps } from '../Question'
import {
	getImageDropdownKey,
	ImageLabeling,
	ClientFields,
	StudentAnswer as StudentAnswerType,
} from '@mission.io/question-toolkit'
import {
	isImageLabeling,
	getChoiceHandlers,
	isDropdownAnswer,
	isStudentDropdownAnswerCorrect,
	Image,
} from '../../utils'
import ImageCanvas from '../../components/image-canvas/ImageCanvas'
import ChoicesResponse from '../../components/choices-response/ChoicesResponse'
import ChoicesReadOnly from '../../components/choices-readonly'
import { QuestionLabel } from '../../components/teacher-header/TeacherHeader'
import EditFooter from '../../components/edit-footer'

import Collapsible from '../../components/collapsible'
import { DropdownTabs as Tabs, TabPanel } from '../../components/dropdown-tabs'
import AnalyticsSingleStudentContainer from '../../components/analytics-single-student/AnalyticsSingleStudent'
import { DropdownImageLabeling } from '../../model/general'
import {
	ReadOnly,
	QuestionResponseStyled,
	TitleBold,
	InputGroup,
	QuestionHeader,
} from '../../assets/styles'
import 'styled-components/macro'
import { styled } from 'styled-components'
import ImageWrapper from '../../components/image-wrapper/ImageWrapper'
import { TextImageField } from '../../components/text-image-field/TextImageField'
import { _tempTheme } from '../../utils/tempTheme'

/**
 * A component for a teacher view where an image labeling question can be created.
 * It also has a readOnly view.
 * @returns {JSX.Element}
 */
export default function Teacher(props: TeacherQuestionProps): JSX.Element {
	const {
		question,
		questionIndex = 0,
		onChange,
		readOnly,
		isQuestionAnalytics,
		studentName,
		studentResponse,
		onRequestClose,
		isCollapsibleQuestion,
		buttonAction,
		autoFocus = false,
	} = props

	if (!question || !isImageLabeling(question)) {
		return <Fragment />
	}

	const {
		handleDeleteChoice,
		handleAddChoice,
		handleAnswerChange,
		handleChange,
	} = getChoiceHandlers(onChange, readOnly)

	const handleDeleteDropDown = (dropdownKey: string) => {
		if (!readOnly && onChange) {
			onChange({
				type: UPDATE_QUESTION_ACTIONS.DELETE_DROPDOWN,
				payload: dropdownKey,
			})
			handleChangeDropdownIndexSelected(0)
		}
	}

	const [dropdownIndexSelected, setDropdownIndexSelected] = useState(0)

	const handleChangeDropdownIndexSelected = (dropdownIndex: number) => {
		setDropdownIndexSelected(dropdownIndex)
		if (question.dropdowns[dropdownIndex])
			document
				.getElementById(getImageDropdownKey(question.dropdowns[dropdownIndex]))
				?.focus()
	}

	useEffect(() => {
		if (
			question.dropdowns.length > 0 &&
			dropdownIndexSelected > -1 &&
			question.dropdowns[dropdownIndexSelected]
		) {
			document
				.getElementById(
					getImageDropdownKey(question.dropdowns[dropdownIndexSelected])
				)
				?.focus()
		}
	}, [question.dropdowns.length])

	if (isQuestionAnalytics)
		return !!onRequestClose ? (
			<AnalyticsSingleStudentContainer
				question={question}
				title="Modeling Question"
				studentResponse={studentResponse}
				onRequestClose={onRequestClose}
				studentName={studentName}>
				<QuestionResponse
					question={question}
					studentAnswer={studentResponse?.answer}
				/>
			</AnalyticsSingleStudentContainer>
		) : (
			<Fragment />
		)

	const [expand, setExpand] = useState(false)

	const handleChangeSelectedTab = (dropdownIndex: number) => {
		setDropdownIndexSelected(dropdownIndex)
		document.getElementById(String(dropdownIndex + 1))?.focus()
	}

	if (isCollapsibleQuestion)
		return (
			<div>
				<Collapsible
					handleClick={() => setExpand(state => !state)}
					expand={expand}
					header={
						<HeaderContent>
							<span className="notranslate">Q: Image Labeling</span>
							<div>
								<Image media={question.media} expandable />
							</div>
						</HeaderContent>
					}
					buttonAction={buttonAction}>
					<div>
						<Tabs
							indexSelected={dropdownIndexSelected}
							question={question}
							handleChange={handleChangeSelectedTab}
						/>
						<TabPanel
							indexSelected={dropdownIndexSelected}
							dropdowns={question.dropdowns}
							readOnly={true}
						/>
					</div>
				</Collapsible>
			</div>
		)

	return readOnly ? (
		<ReadOnly className="Question">
			<div>
				<QuestionHeader>
					<span>
						<strong className="notranslate">
							{`Q${questionIndex + 1}.`}&nbsp;&nbsp;
						</strong>
					</span>
					Curriculum Questions - Image Labeling
				</QuestionHeader>
				<ImageWrapper
					question={question}
					isReadonly
					className="max-w-72 m-auto"
				/>
			</div>

			<div className="flex flex-wrap gap-2">
				{question.dropdowns.map(
					(item: DropdownImageLabeling, index: number) => (
						<ChoicesReadOnly
							key={`dropdown-${index}`}
							choices={item.choices}
							index={index}
						/>
					)
				)}
			</div>
		</ReadOnly>
	) : (
		<>
			<div className="bg-brand-cloud-blue text-brand-space-blue rounded-t-lg p-4">
				<div>
					<div className="font-semibold text-start mb-4">Image Labeling</div>
					<InputGroup className="my-2">
						<QuestionLabel>
							<span className="notranslate">{`Q${questionIndex + 1}`}</span>
						</QuestionLabel>
						<TextImageField
							css="margin-right: 34px; border-top-left-radius: 0px; border-bottom-left-radius: 0px;"
							value={question.phrase}
							onChange={(e: any) =>
								onChange &&
								onChange({
									type: UPDATE_QUESTION_ACTIONS.CHANGE_QUESTION,
									payload: e.target?.value,
								})
							}
							autoFocus={autoFocus}
						/>
					</InputGroup>
					<span className="font-light text-sm">
						Click on the image where you want to add your question
					</span>
				</div>
				<ImageCanvas
					questionMedia={question.media}
					onChange={onChange}
					readOnly={Boolean(readOnly)}
					dropdowns={question.dropdowns}
					setDropdownIndexSelected={(index: number) =>
						handleChangeDropdownIndexSelected(index)
					}
					imageProperties={question.imageProperties}
				/>
				{question.dropdowns.length > 0 && (
					<div>
						<Tabs
							indexSelected={dropdownIndexSelected}
							question={question}
							handleChange={handleChangeDropdownIndexSelected}
							handleClose={handleDeleteDropDown}
						/>
						<TabPanel
							dropdowns={question.dropdowns}
							indexSelected={dropdownIndexSelected}
							handleAddChoice={handleAddChoice}
							handleAnswerChange={handleAnswerChange}
							handleDeleteChoice={handleDeleteChoice}
							handleChange={handleChange}
						/>
					</div>
				)}
			</div>
			<EditFooter onChange={onChange} />
		</>
	)
}

/**
 * This function makes the answer box for Analytics Student answer.
 * We just output the student answer.
 */
function QuestionResponse({
	question,
	studentAnswer,
}: {
	question: ImageLabeling<ClientFields>
	studentAnswer?: StudentAnswerType
}): JSX.Element {
	if (studentAnswer && !isDropdownAnswer(studentAnswer)) return <Fragment />

	return (
		<QuestionResponseStyled css="background: transparent;">
			{question.dropdowns.map(
				(dropdown: DropdownImageLabeling, index: number) => {
					const isCorrect = isStudentDropdownAnswerCorrect(
						dropdown,
						getImageDropdownKey(dropdown),
						studentAnswer
					)
					return (
						<li className="dropdown-wrapper" key={index}>
							<TitleBold>{`${index + 1}. ${
								isCorrect ? 'Correct' : 'Incorrect'
							}`}</TitleBold>
							<ChoicesResponse
								choices={dropdown.choices}
								studentChoiceSelected={String(
									studentAnswer?.value[getImageDropdownKey(dropdown)]
								)}
							/>
						</li>
					)
				}
			)}
		</QuestionResponseStyled>
	)
}

export const HeaderContent = styled.div`
	display: flex;
	align-items: center;
	width: 100%;
	text-align: left;
	justify-content: space-between;
	margin-right: ${_tempTheme.spacing2};

	img {
		max-width: 50px;
	}
`
